import "./login.css";
import { useNavigate } from "react-router-dom";
import { LockOutlined, UserOutlined, LoginOutlined } from "@ant-design/icons";
import { Button, Form, Input, Flex } from "antd";
import { useAuth, useLanguage, useWeb } from "../../../hooks";
import { AuthServices, ServiceResponseProps } from "../../../services";
import { authActions, webActions } from "../../../context";
import { useEffect, useState } from "react";
import {
  Edit,
  FilterProps,
  GuestHeader,
  Icon,
  Modal,
  Panel,
  Spacer,
  StringMap,
  Toggle,
} from "../../../components";
import { RouterEnum } from "../../../router";
import {
  CheckmarkCircleOutline,
  HeadphonesFill,
  ReportPanelSVG,
} from "../../../assets";
import { FormFields, MIN_PASSWORD_LENGTH } from "./form.props";

const LoginScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { dispatch: dispatchWeb } = useWeb();
  const { dispatch: dispatchAuth } = useAuth();
  const { signInService } = AuthServices();
  const { setLoading } = webActions(dispatchWeb);
  const { setLogin, setToken, setRefreshToken, setUser, setRol } =
    authActions(dispatchAuth);

  const initialFormFields: FormFields = {
    email: "",
    password: "",
    remember: true,
  };
  const [form, setForm] = useState<FormFields>(initialFormFields);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true);
  const [error, setError] = useState<ServiceResponseProps>({
    error: false,
    messageError: "",
  });
  const [helpModalShow, setHelpModalShow] = useState<boolean>(false);
  const [contactModalShow, setContactModalShow] = useState<boolean>(false);
  const [contactButtonDisabled, setContactButtonDisabled] =
    useState<boolean>(false);
  const [contactData, setContactData] = useState<StringMap>({});
  const [sentSuccess, setSentSuccess] = useState<boolean>(false);

  const handleFieldsChange = (value: any) => {
    const e = value[0];
    setForm({ ...form, [e?.name?.[0]]: e?.value });

    if (
      e?.errors?.length > 0 ||
      form?.email?.length == 0 ||
      form?.password?.length < MIN_PASSWORD_LENGTH
    ) {
      setIsSubmitDisabled(true);
    } else {
      setIsSubmitDisabled(false);
    }
  };

  const handleRemember = (state: boolean) =>
    setForm({ ...form, remember: state });

  const onFinish = async (values: {
    email: string;
    password: string;
    remember: boolean;
  }) => {
    setLoading({ isLoading: true });
    try {
      const data = await signInService(values);

      if (data.error) {
        setError({
          error: data.error,
          messageError:
            data.typeError === "emailOrPasswordError"
              ? t.loginScreen.errors.emailOrPasswordError
              : data.typeError === "unexpectedError"
              ? t.global.unexpectedError
              : data.messageError,
        });
      } else {
        setLogin(true, values.remember);
        setToken(data.data.token);
        setRefreshToken(data.data.refreshToken);
        setUser(data.data.user);
        setRol(data.data.roles);
        navigate(RouterEnum.DASHBOARD);
      }
    } catch (error) {
      setError({
        error: true,
        messageError: t.global.unexpectedError,
      });
    } finally {
      setLoading({ isLoading: false });
    }
  };

  useEffect(() => {
    if (sentSuccess) {
      const closeModalContactUs = setTimeout(() => {
        setContactModalShow(false);
        const resetContactUs = setTimeout(() => {
          setContactData({});
          setContactButtonDisabled(false);
          setSentSuccess(false);

          return () => clearTimeout(resetContactUs);
        }, 200);
      }, 2000);

      return () => clearTimeout(closeModalContactUs);
    }
  }, [sentSuccess]);

  return (
    <>
      <Panel header={false}>
        <GuestHeader />

        <section className="login-container">
          <Spacer />
          <img src={ReportPanelSVG} alt="Report Panel" />
          <h1>{t.loginScreen.signIn}</h1>

          <Form
            name="login"
            initialValues={{ remember: true }}
            style={{ minWidth: 200, width: 410, maxWidth: "95%" }}
            onFieldsChange={handleFieldsChange}
            onFinish={onFinish}
          >
            <Form.Item
              className="login-fix-spacer"
              name="email"
              rules={[
                {
                  required: true,
                  message: `${
                    t.loginScreen.errors.fieldInvalid
                  } ${t.loginScreen.email.toLowerCase()}`,
                },
                {
                  type: "email",
                  pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: `${t.loginScreen.email} ${t.loginScreen.errors.invalid}`,
                },
              ]}
            >
              <Input
                className="login-input"
                prefix={<UserOutlined />}
                placeholder={t.loginScreen.email}
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                {
                  required: true,
                  message: `${
                    t.loginScreen.errors.fieldInvalid
                  } ${t.loginScreen.password.toLowerCase()}`,
                },
              ]}
            >
              <Input.Password
                className="login-input"
                prefix={<LockOutlined />}
                type="password"
                placeholder={t.loginScreen.password}
              />
            </Form.Item>

            <Form.Item>
              <Flex justify="space-between" align="center">
                <Toggle
                  checked={form.remember}
                  onChange={(state) => handleRemember(state)}
                  text={t.loginScreen.remember}
                />
                {/* <a href="">Forgot password</a> */}
              </Flex>
            </Form.Item>

            <Form.Item>
              <Button
                block
                type="primary"
                className={`login-submit ${isSubmitDisabled ? "disabled" : ""}`}
                htmlType="submit"
                disabled={isSubmitDisabled}
              >
                <LoginOutlined /> {t.loginScreen.signIn}
              </Button>

              <span className="login-error">{error.messageError}</span>
            </Form.Item>
          </Form>

          <Spacer />
          <div className="login-help-container">
            <nav className="login-help-nav-wrapper">
              <a
                href="https://google.com"
                target="_blank"
                className="login-help-nav"
              >
                <p>{t.loginScreen.help.generalConditions}</p>
              </a>
              <div className="login-help-divider" />
              <a
                href="https://google.com"
                target="_blank"
                className="login-help-nav"
              >
                <p>{t.loginScreen.help.privacyPolicy}</p>
              </a>
            </nav>
            <button
              className="login-help-headphones-container"
              onClick={() => setHelpModalShow(true)}
            >
              <Icon
                icon={HeadphonesFill}
                alt="Icon Headphones"
                className="login-help-headphones-icon"
              />
            </button>
          </div>
        </section>

        <Modal
          open={helpModalShow}
          setOpen={setHelpModalShow}
          title={t.loginScreen.help.helpModal.title}
          titleStyles={{
            contentAlign: "center",
            textAlign: "center",
          }}
          actions={[
            {
              text: t.loginScreen.help.helpModal.contact,
              onClick() {
                setHelpModalShow(false);
                setContactModalShow(true);
              },
            },
            {
              text: t.loginScreen.help.helpModal.call,
              onClick() {},
            },
          ]}
        >
          <p className="login-help-modal-description">
            {t.loginScreen.help.helpModal.description}
          </p>
        </Modal>

        <Modal
          open={contactModalShow}
          setOpen={setContactModalShow}
          title={!sentSuccess && t.loginScreen.help.contactModal.title}
          className={`login-contactus-sent ${sentSuccess ? "success" : ""}`}
          actions={
            sentSuccess
              ? undefined
              : [
                  {
                    text: t.loginScreen.help.contactModal.send,
                    onClick() {
                      setSentSuccess(true);
                    },
                    disabled: contactButtonDisabled,
                  },
                ]
          }
        >
          <>
            {sentSuccess ? (
              <div className="login-contactus-sent-success-wrapper">
                <Icon
                  icon={CheckmarkCircleOutline}
                  alt="Sent success"
                  className="login-contactus-sent-success-icon"
                />
                <p className="login-contactus-sent-success">
                  {t.loginScreen.help.contactModal.success}
                </p>
              </div>
            ) : (
              <ContactForm data={contactData} setData={setContactData} />
            )}
          </>
        </Modal>
      </Panel>
    </>
  );
};

export default LoginScreen;

enum ContactEnum {
  NAME = "name",
  EMAIL = "email",
  PHONE = "phone",
  MESSAGE = "message",
}

interface ContactFormProps {
  data: StringMap;
  setData: React.Dispatch<React.SetStateAction<StringMap>>;
}

const ContactForm: React.FC<ContactFormProps> = ({ data, setData }) => {
  const { t } = useLanguage();

  const handleChange = (data: FilterProps) => {
    const { key, value } = data;

    setData((prevData) => {
      return { ...prevData, [key]: value };
    });
  };

  return (
    <Edit>
      {/* NAME */}
      <Edit.Input
        id={ContactEnum.NAME}
        value={data[ContactEnum.NAME]}
        placeholder={t.loginScreen.help.contactModal.name}
        onChange={(e) =>
          handleChange({ key: e.target.id, value: e.target.value })
        }
      />
      {/* EMAIL */}
      <Edit.Input
        id={ContactEnum.EMAIL}
        value={data[ContactEnum.EMAIL]}
        placeholder={t.loginScreen.help.contactModal.email}
        onChange={(e) =>
          handleChange({ key: e.target.id, value: e.target.value })
        }
      />
      {/* PHONE */}
      <Edit.Input
        id={ContactEnum.PHONE}
        value={data[ContactEnum.PHONE]}
        placeholder={t.loginScreen.help.contactModal.phone}
        onChange={(e) =>
          handleChange({ key: e.target.id, value: e.target.value })
        }
      />
      {/* MESSAGE */}
      <Edit.TextArea
        id={ContactEnum.MESSAGE}
        value={data[ContactEnum.MESSAGE]}
        placeholder={t.loginScreen.help.contactModal.message}
        onChange={(e) =>
          handleChange({ key: e.target.id, value: e.target.value })
        }
      />
    </Edit>
  );
};
